import { useEffect, useMemo, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Building2, Loader2, Search, X } from 'lucide-react'
import { initializeMyProjects } from 'slices/projectSlice'
import MobileNavCard from 'components/MobileNavCard/MobileNavCard'
import FieldShell from '../components/FieldShell'
import FieldAvatar from '../components/FieldAvatar'
import { fieldRoutes } from 'constants/fieldRoutes'

/**
 * Every project the operator owns, searchable.
 *
 * A flat searchable list rather than a proximity one: a project is not
 * somewhere you stand. It is the context you are working in — "which client am
 * I touring for" — and the thing you reach for when catching up on a laptop's
 * worth of work without the laptop.
 */
const FieldProjects = () => {
	const dispatch = useDispatch()
	const [query, setQuery] = useState('')

	const { entities, ids, isLoading } = useSelector((state: any) => state.projects)

	useEffect(() => {
		;(dispatch as any)(initializeMyProjects())
	}, [dispatch])

	const projects = useMemo(() => {
		const all = (ids || []).map((id: string) => entities[id]).filter(Boolean)
		const needle = query.trim().toLowerCase()
		if (!needle) return all
		return all.filter(
			(project: any) =>
				(project.projectName || '').toLowerCase().includes(needle) ||
				(project.projectType || '').toLowerCase().includes(needle) ||
				(project.projectDescription || '').toLowerCase().includes(needle),
		)
	}, [entities, ids, query])

	return (
		<FieldShell title="Projects">
			<div className="pb-6">
				<div className="sticky top-0 z-20 bg-gray-50 px-3 py-2">
					<div className="relative">
						<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
						<input
							value={query}
							onChange={e => setQuery(e.target.value)}
							placeholder="Search projects"
							aria-label="Search projects"
							className="w-full rounded-full border border-gray-300 bg-white py-2 pl-9 pr-9 text-base"
							// 16px or larger, or iOS zooms the page on focus.
							style={{ fontSize: '16px' }}
							autoCapitalize="none"
							autoCorrect="off"
							spellCheck={false}
						/>
						{query && (
							<button
								type="button"
								onClick={() => setQuery('')}
								aria-label="Clear search"
								className="absolute right-1 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-gray-400"
							>
								<X className="h-4 w-4" />
							</button>
						)}
					</div>
				</div>

				{isLoading && projects.length === 0 ? (
					<p className="flex items-center gap-2 px-4 py-6 text-sm text-gray-500">
						<Loader2 className="w-4 h-4 animate-spin" />
						Loading projects…
					</p>
				) : projects.length === 0 ? (
					<div className="m-3 rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center">
						<p className="text-sm font-medium text-gray-800">
							{query ? `Nothing matches "${query}"` : 'No projects yet'}
						</p>
						{query && (
							<p className="mt-1 text-sm text-gray-500">
								Search looks at names, types and descriptions.
							</p>
						)}
					</div>
				) : (
					<div className="flex flex-col gap-3 p-3">
						{projects.map((project: any) => (
							<MobileNavCard
								key={project._id}
								to={generatePath(fieldRoutes.PROJECT, { projectId: project._id })}
								/*
									The project's location, photographed. `projectAsset` IS the
									project's location, and it arrives from /myprojects already
									populated with its image. A branch glyph on every card said
									only "this is a project", which the screen title covers.
								*/
								icon={
									<FieldAvatar
										src={project.projectAsset?.image}
										fallbackIcon={Building2}
										label={project.projectAsset?.assetName || 'Project location'}
									/>
								}
								label={project.projectName || 'Untitled project'}
								hint={project.projectType || project.projectDescription || undefined}
							/>
						))}
					</div>
				)}
			</div>
		</FieldShell>
	)
}

export default FieldProjects
